import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'theme-storage';
const LEGACY_THEME_KEY = 'pairtab-theme';

const getSystemPrefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolveIsDark = (theme: ThemeMode) =>
  theme === 'dark' || (theme === 'system' && getSystemPrefersDark());

const applyThemeToDocument = (theme: ThemeMode) => {
  if (typeof document === 'undefined') return;
  const isDark = resolveIsDark(theme);
  document.documentElement.classList.toggle('dark', isDark);
  document.documentElement.dataset.theme = isDark ? 'dark' : 'light';
  const themeColor = isDark ? '#0D1B2A' : '#F5F9F7';
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor);
};

const readLegacyTheme = (): ThemeMode | null => {
  if (typeof window === 'undefined') return null;
  const legacyTheme = window.localStorage.getItem(LEGACY_THEME_KEY);
  return legacyTheme === 'light' || legacyTheme === 'dark' || legacyTheme === 'system'
    ? legacyTheme
    : null;
};

const readPersistedTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'system';

  const legacyTheme = readLegacyTheme();
  if (legacyTheme) return legacyTheme;

  const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (!raw) return 'system';

  try {
    const parsed = JSON.parse(raw);
    const storedTheme = parsed?.state?.theme;
    return storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'system'
      ? storedTheme
      : 'system';
  } catch {
    return 'system';
  }
};

interface ThemeState {
  theme: ThemeMode;
  isDark: boolean;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  initTheme: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: readPersistedTheme(),
      isDark: resolveIsDark(readPersistedTheme()),

      setTheme: (theme) => {
        const isDark = resolveIsDark(theme);
        set({ theme, isDark });
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(LEGACY_THEME_KEY);
        }
        applyThemeToDocument(theme);
      },

      toggleTheme: () => {
        const newTheme = get().isDark ? 'light' : 'dark';
        set({ theme: newTheme, isDark: !get().isDark });
        applyThemeToDocument(newTheme);
      },

      initTheme: () => {
        const theme = readPersistedTheme();
        const isDark = resolveIsDark(theme);
        set({ isDark });
        if (typeof window !== 'undefined') {
          const legacyTheme = readLegacyTheme();
          if (legacyTheme) {
            window.localStorage.removeItem(LEGACY_THEME_KEY);
          }
        }
        applyThemeToDocument(theme);
      }
    }),
    {
      name: THEME_STORAGE_KEY
    }
  )
);
